import { useMemo } from 'react';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { AreaChartData } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { commonApiOptions } from '../../helpers/api-helpers';
import { useRoiRequestPayload, type UseRoiRequestBaseProps } from '../../hooks/use-roi-request-payload';
import { appointmentsScheduleRate } from '../demo-data/appointments-schedule-rate';

type HighestConversion = {
  difference: number;
  timePeriod: string;
  timeType: string;
};

interface UseFetchBTRoiScheduleRate {
  isLoading?: boolean;
  scheduleRate: {
    chartDataGroups: AreaChartData['groups'];
    dates: Record<string, number>;
    difference: number;
    highestConversion: HighestConversion;
    showWeaveAverageGraph?: boolean;
    weaveAverage: number;
  };
}

export const useFetchBTRoiScheduleRate = (props: UseRoiRequestBaseProps): UseFetchBTRoiScheduleRate => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.bulkTextRoiDemoData);

  const { request, skipRequest } = useRoiRequestPayload({
    ...props,
    additionalRequestPayload: {
      roiType: RoiType.MARKETING_MESSAGE,
    },
    isDemoAccount,
  });

  const { data: scheduleRateData, isLoading } = AnalyticsGatewayV1.Queries.useGetScheduleConversionQuery({
    request,
    options: {
      ...commonApiOptions,
      enabled: !skipRequest,
      onError: () => {
        alert.error(t("Couldn't fetch the appointment schedule rate details. Please try again."));
      },
    },
  });

  const processedData = useMemo(() => {
    const scheduleRate: UseFetchBTRoiScheduleRate['scheduleRate'] = {
      chartDataGroups: [],
      dates: {},
      difference: 0,
      highestConversion: {
        difference: 0,
        timePeriod: '',
        timeType: '',
      },
      showWeaveAverageGraph: false,
      weaveAverage: 0,
    };

    const response = isDemoAccount ? appointmentsScheduleRate() : scheduleRateData;

    if (!response) {
      return {
        scheduleRate,
      };
    }

    const data = response.data;

    scheduleRate.difference = (data?.rateDifference || 0) * 100;
    scheduleRate.weaveAverage = (data?.weaveAverage || 0) * 100;
    scheduleRate.chartDataGroups =
      data?.dates?.map(({ key = '', rate = 0, weaveAverage = 0 }) => {
        if (rate >= weaveAverage) {
          const conversionDifference = (weaveAverage - rate) * 100;

          if (conversionDifference > scheduleRate.highestConversion.difference) {
            scheduleRate.highestConversion = {
              difference: (weaveAverage - rate) * 100,
              timePeriod: key,
              timeType: data?.timePeriod || '',
            };
          }
        }

        scheduleRate.dates[key] = rate * 100;

        return {
          name: key,
          values: {
            yourScheduleRate: rate * 100,
          },
        };
      }) || [];

    // TODO :: Enable this condition when the PM and team has confidence over the data being showed
    // scheduleRate.showWeaveAverageGraph = scheduleRate.difference > 0;

    return {
      scheduleRate,
    };
  }, [isDemoAccount, scheduleRateData]);

  return {
    ...processedData,
    isLoading,
  };
};
